import { UnitSystem } from '../contexts/SettingsContext';

/**
 * Convert temperature from Celsius to selected unit system
 */
export const convertTemperature = (celsius: number, unitSystem: UnitSystem): number => {
  if (unitSystem === 'imperial') {
    return (celsius * 9/5) + 32; // Celsius to Fahrenheit
  }
  return celsius;
};

/**
 * Get temperature unit symbol
 */
export const getTemperatureUnit = (unitSystem: UnitSystem): string => {
  return unitSystem === 'imperial' ? '°F' : '°C';
};

/**
 * Convert wind speed from m/s to selected unit system
 */
export const convertWindSpeed = (ms: number, unitSystem: UnitSystem): number => {
  if (unitSystem === 'imperial') {
    return ms * 2.237; // m/s to mph
  }
  return ms; // Keep m/s for metric
};

/**
 * Get wind speed unit symbol
 */
export const getWindSpeedUnit = (unitSystem: UnitSystem): string => {
  return unitSystem === 'imperial' ? 'mph' : 'm/s';
};

/**
 * Convert pressure from hPa to selected unit system
 */
export const convertPressure = (hpa: number, unitSystem: UnitSystem): number => {
  if (unitSystem === 'imperial') {
    return hpa * 0.02953; // hPa to inHg
  }
  return hpa;
};

/**
 * Get pressure unit symbol
 */
export const getPressureUnit = (unitSystem: UnitSystem): string => {
  return unitSystem === 'imperial' ? 'inHg' : 'hPa';
};

/**
 * Convert visibility from meters to selected unit system
 */
export const convertVisibility = (meters: number, unitSystem: UnitSystem): number => {
  if (unitSystem === 'imperial') {
    return meters * 0.000621371; // meters to miles
  }
  return meters / 1000; // meters to kilometers
};

/**
 * Get visibility unit symbol
 */
export const getVisibilityUnit = (unitSystem: UnitSystem): string => {
  return unitSystem === 'imperial' ? 'mi' : 'km';
};

/**
 * Convert precipitation from mm to selected unit system
 */
export const convertPrecipitation = (mm: number, unitSystem: UnitSystem): number => {
  if (unitSystem === 'imperial') {
    return mm * 0.0393701; // mm to inches
  }
  return mm;
};

/**
 * Get precipitation unit symbol
 */
export const getPrecipitationUnit = (unitSystem: UnitSystem): string => {
  return unitSystem === 'imperial' ? 'in' : 'mm';
};

/**
 * Format temperature with unit
 */
export const formatTemperature = (celsius: number, unitSystem: UnitSystem): string => {
  const temp = convertTemperature(celsius, unitSystem);
  return `${Math.round(temp)}${getTemperatureUnit(unitSystem)}`;
};

/**
 * Format wind speed with unit
 */
export const formatWindSpeed = (ms: number, unitSystem: UnitSystem): string => {
  const speed = convertWindSpeed(ms, unitSystem);
  return `${Math.round(speed)} ${getWindSpeedUnit(unitSystem)}`;
};

/**
 * Format pressure with unit
 */
export const formatPressure = (hpa: number, unitSystem: UnitSystem): string => {
  const pressure = convertPressure(hpa, unitSystem);
  return unitSystem === 'imperial' 
    ? `${pressure.toFixed(2)} ${getPressureUnit(unitSystem)}`
    : `${Math.round(pressure)} ${getPressureUnit(unitSystem)}`;
};

/**
 * Format visibility with unit
 */
export const formatVisibility = (meters: number, unitSystem: UnitSystem): string => {
  const visibility = convertVisibility(meters, unitSystem);
  return `${visibility.toFixed(1)} ${getVisibilityUnit(unitSystem)}`;
};

/**
 * Format precipitation with unit
 */
export const formatPrecipitation = (mm: number, unitSystem: UnitSystem): string => {
  const precip = convertPrecipitation(mm, unitSystem);
  return unitSystem === 'imperial'
    ? `${precip.toFixed(2)} ${getPrecipitationUnit(unitSystem)}`
    : `${precip.toFixed(1)} ${getPrecipitationUnit(unitSystem)}`;
};

// ==================== WEIGHT CONVERSION (for fishing catches) ====================
// Database always stores weight in grams (g)

/**
 * Convert grams to kg
 */
export const gramsToKg = (grams: number): number => {
  return grams / 1000;
};

/**
 * Convert kg to grams
 */
export const kgToGrams = (kg: number): number => {
  return Math.round(kg * 1000);
};

/**
 * Convert grams to pounds
 */
export const gramsToPounds = (grams: number): number => {
  return grams * 0.00220462;
};

/**
 * Convert pounds to grams
 */
export const poundsToGrams = (pounds: number): number => {
  return Math.round(pounds / 0.00220462);
};

/**
 * Convert grams to display weight based on unit system
 */
export const convertWeight = (grams: number, unitSystem: UnitSystem): number => {
  if (unitSystem === 'imperial') {
    return gramsToPounds(grams);
  }
  return gramsToKg(grams);
};

/**
 * Convert display weight to grams based on unit system
 */
export const displayToGrams = (displayWeight: number, unitSystem: UnitSystem): number => {
  if (unitSystem === 'imperial') {
    return poundsToGrams(displayWeight);
  }
  return kgToGrams(displayWeight);
};

/**
 * Get weight unit label
 */
export const getWeightUnit = (unitSystem: UnitSystem): string => {
  return unitSystem === 'imperial' ? 'lbs' : 'kg';
};

/**
 * Format weight for display
 */
export const formatWeight = (grams: number | undefined, unitSystem: UnitSystem): string => {
  if (!grams) return '-';
  const value = convertWeight(grams, unitSystem);
  const unit = getWeightUnit(unitSystem);
  return `${value.toFixed(2)} ${unit}`;
};

// ==================== LENGTH CONVERSION (for fishing catches) ====================
// Database always stores length in millimeters (mm)

/**
 * Convert mm to cm
 */
export const mmToCm = (mm: number): number => {
  return mm / 10;
};

/**
 * Convert cm to mm
 */
export const cmToMm = (cm: number): number => {
  return Math.round(cm * 10);
};

/**
 * Convert mm to inches
 */
export const mmToInches = (mm: number): number => {
  return mm * 0.0393701;
};

/**
 * Convert inches to mm
 */
export const inchesToMm = (inches: number): number => {
  return Math.round(inches / 0.0393701);
};

/**
 * Convert mm to display length based on unit system
 */
export const convertLength = (mm: number, unitSystem: UnitSystem): number => {
  if (unitSystem === 'imperial') {
    return mmToInches(mm);
  }
  return mmToCm(mm);
};

/**
 * Convert display length to mm based on unit system
 */
export const displayToMm = (displayLength: number, unitSystem: UnitSystem): number => {
  if (unitSystem === 'imperial') {
    return inchesToMm(displayLength);
  }
  return cmToMm(displayLength);
};

/**
 * Get length unit label
 */
export const getLengthUnit = (unitSystem: UnitSystem): string => {
  return unitSystem === 'imperial' ? 'in' : 'cm';
};

/**
 * Format length for display
 */
export const formatLength = (mm: number | undefined, unitSystem: UnitSystem): string => {
  if (!mm) return '-';
  const value = convertLength(mm, unitSystem);
  const unit = getLengthUnit(unitSystem);
  return `${value.toFixed(1)} ${unit}`;
};

// ==================== DEPTH CONVERSION (for fishing depth) ====================
// Database always stores depth in millimeters (mm)

/**
 * Convert mm to meters
 */
export const mmToMeters = (mm: number): number => {
  return mm / 1000;
};

/**
 * Convert meters to mm
 */
export const metersToMm = (meters: number): number => {
  return Math.round(meters * 1000);
};

/**
 * Convert mm to feet
 */
export const mmToFeet = (mm: number): number => {
  return (mm / 1000) * 3.28084; // mm -> meters -> feet
};

/**
 * Convert feet to mm
 */
export const feetToMm = (feet: number): number => {
  return Math.round((feet / 3.28084) * 1000); // feet -> meters -> mm
};

/**
 * Convert depth from mm to display units
 */
export const convertDepth = (mm: number, unitSystem: UnitSystem): number => {
  return unitSystem === 'imperial' ? mmToFeet(mm) : mmToMeters(mm);
};

/**
 * Get depth unit symbol
 */
export const getDepthUnit = (unitSystem: UnitSystem): string => {
  return unitSystem === 'imperial' ? 'ft' : 'm';
};

/**
 * Convert display depth to mm (for database storage)
 */
export const displayToMmDepth = (displayValue: number, unitSystem: UnitSystem): number => {
  return unitSystem === 'imperial' ? feetToMm(displayValue) : metersToMm(displayValue);
};

/**
 * Format depth for display
 */
export const formatDepth = (mm: number | undefined, unitSystem: UnitSystem): string => {
  if (!mm) return '-';
  const value = convertDepth(mm, unitSystem);
  const unit = getDepthUnit(unitSystem);
  return `${value.toFixed(1)} ${unit}`;
};
